import { useCallback, useEffect, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Community Dispatch Panel — surfaces the top-3 qualified solo workers
 * per the Equitable Distribution Mandate (longest idle → community
 * tags → capacity limit). One-tap DISPATCH creates a dispatch_log
 * entry, seeds a payout_ledger row, and logs the autonomous COO
 * decision.
 */
const CommunityDispatchPanel = () => {
  const [top, setTop] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dispatching, setDispatching] = useState(null);
  const [track, setTrack] = useState("tier_2_standard");
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/dispatch/surface`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ top_n: 3 }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setTop(d.top_workers || []);
      setMeta({ scanned: d.workforce_scanned, available: d.available_after_safeguards });
      setErr("");
    } catch (e) {
      setErr(e.message || "Could not surface workforce.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dispatch = async (workerId) => {
    setDispatching(workerId);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/dispatch/assign`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ worker_id: workerId, turn_track: track }),
      });
      if (r.ok) {
        await load();
      } else {
        alert("Dispatch failed. See Vibe Board for details.");
      }
    } catch { alert("Network error dispatching."); }
    finally { setDispatching(null); }
  };

  if (loading && top.length === 0) return null;
  if (err && top.length === 0) return null; // fail-quiet

  return (
    <div data-testid="community-dispatch-panel" style={{
      background: "linear-gradient(135deg, rgba(232,184,196,0.1), rgba(184,145,90,0.08))",
      border: "1px solid rgba(232,184,196,0.45)",
      borderRadius: 8,
      padding: "0.9rem 1.1rem",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "0.6rem", marginBottom: "0.7rem" }}>
        <div>
          <div style={{ color: "#E8B8C4", fontSize: "0.68rem", letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 800 }}>
            Community Dispatch · Needs Your YES
          </div>
          <div data-testid="dispatch-headline" style={{ color: "#F3F4F6", fontSize: "0.92rem", fontWeight: 700, marginTop: 3 }}>
            Top {top.length} qualified solo workers · Equitable Distribution mandate
          </div>
          {meta && (
            <div style={{ color: "#ADBED1", fontSize: "0.76rem", marginTop: 2 }}>
              Scanned {meta.scanned} approved · {meta.available} available after capacity safeguards
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <select
            data-testid="dispatch-track"
            value={track}
            onChange={(e) => setTrack(e.target.value)}
            style={{ background: "rgba(0,0,0,0.28)", color: "#F5EFE1",
                     border: "1px solid rgba(232,184,196,0.4)", borderRadius: 4,
                     padding: "0.3rem 0.55rem", fontSize: "0.75rem", fontWeight: 700 }}>
            <option value="tier_1_rapid">T1 · 48-Hour Rapid</option>
            <option value="tier_2_standard">T2 · 14-Day Standard</option>
            <option value="tier_3_full">T3 · 21-Day Full Dignity</option>
          </select>
          <button type="button" onClick={load} data-testid="dispatch-refresh"
                  style={{ background: "transparent", border: "1px solid #E8B8C4", color: "#E8B8C4",
                           padding: "0.3rem 0.7rem", borderRadius: 4, fontSize: "0.72rem", fontWeight: 700, cursor: "pointer" }}>
            🔄 Re-scan
          </button>
        </div>
      </div>

      {top.length === 0 && (
        <div data-testid="dispatch-empty" style={{ color: "#ADBED1", fontSize: "0.82rem", padding: "0.7rem 0.3rem", fontStyle: "italic" }}>
          No approved workers yet. Approve a vendor application or a Hunter prospect and they surface here immediately.
        </div>
      )}

      <div style={{ display: "grid", gap: "0.5rem" }}>
        {top.map((w, i) => {
          const p = w.pills || {};
          const idleColor = p.idle_days_alert ? "#F87171" : p.idle_days_value > 0 ? "#FFB347" : "#00DC78";
          return (
            <div key={w.worker_id} data-testid={`dispatch-worker-${w.worker_id}`}
                 style={{ background: "rgba(0,0,0,0.24)", padding: "0.65rem 0.85rem",
                          borderRadius: 5, borderLeft: `3px solid ${idleColor}`,
                          display: "grid", gap: "0.4rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: "0.6rem", flexWrap: "wrap" }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ color: "#F3F4F6", fontSize: "0.94rem", fontWeight: 700 }}>
                    #{i + 1} · {w.business_name}
                    {p.community_first_verified && (
                      <span data-testid={`pill-community-${w.worker_id}`}
                            style={{ marginLeft: 6, fontSize: "0.68rem", color: "#E8B8C4",
                                     background: "rgba(232,184,196,0.15)", padding: "2px 7px",
                                     borderRadius: 10, fontWeight: 700, letterSpacing: "0.1em" }}>
                        ⭐ COMMUNITY-FIRST
                      </span>
                    )}
                  </div>
                  <div style={{ color: "#ADBED1", fontSize: "0.76rem", marginTop: 2 }}>
                    {(w.trades || []).map((t) => t.replace("_", "-")).join(" · ") || "—"} · {w.city || "—"} · Tier {w.tier || "?"}
                    {w.email && <> · {w.email}</>}
                  </div>
                </div>
                <button type="button"
                        data-testid={`dispatch-${w.worker_id}`}
                        onClick={() => dispatch(w.worker_id)}
                        disabled={dispatching === w.worker_id}
                        style={{ background: dispatching === w.worker_id
                                    ? "rgba(0,220,120,0.35)"
                                    : "linear-gradient(90deg, #00DC78, #00B364)",
                                 color: "#062617", border: "none",
                                 padding: "0.5rem 1rem", borderRadius: 5,
                                 fontSize: "0.82rem", fontWeight: 800,
                                 cursor: dispatching === w.worker_id ? "not-allowed" : "pointer",
                                 whiteSpace: "nowrap" }}>
                  {dispatching === w.worker_id ? "Dispatching…" : "🎯 DISPATCH"}
                </button>
              </div>
              {/* Pills */}
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <span data-testid={`pill-idle-${w.worker_id}`}
                      style={{ background: `${idleColor}20`, border: `1px solid ${idleColor}55`,
                               color: idleColor, padding: "2px 8px", borderRadius: 10,
                               fontSize: "0.7rem", fontWeight: 700 }}>
                  {(() => {
                    const d = p.idle_days_value;
                    // 9999 is the "never dispatched" sentinel from the backend.
                    // Show friendly text instead of the raw number so purse
                    // holders don't see a broken-looking "Idle 9999d".
                    if (d == null || d >= 9000) return "Awaiting first dispatch";
                    return `${p.idle_days_alert ? "🔔 " : ""}Idle ${d}d`;
                  })()}
                </span>
                <span data-testid={`pill-velocity-${w.worker_id}`}
                      style={{ background: "rgba(90,200,216,0.15)", border: "1px solid rgba(90,200,216,0.45)",
                               color: "#5AC8D8", padding: "2px 8px", borderRadius: 10,
                               fontSize: "0.7rem", fontWeight: 700 }}>
                  ⏱ Est. {p.estimated_turn_velocity_hours <= 48 ? "48-hr" : `${Math.round((p.estimated_turn_velocity_hours || 0) / 24)}-day`} turn
                </span>
                {p.community_tag_count > 0 && (
                  <span style={{ background: "rgba(232,184,196,0.15)", border: "1px solid rgba(232,184,196,0.45)",
                                 color: "#E8B8C4", padding: "2px 8px", borderRadius: 10,
                                 fontSize: "0.7rem", fontWeight: 700 }}>
                    {p.community_tag_count} tag{p.community_tag_count > 1 ? "s" : ""}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CommunityDispatchPanel;
